import { Route } from '@angular/router';
import { IsLoggedInGuard, RequiresPermissionGuard } from '@lexplus-authentication';
import { LexplusPermissionsEnum } from '@lexplus-authentication-api';
import { LOCALIZED_USER_ROUTES, USER_ROUTES_IDS } from '../../localized-user-routes-const';

export function AdministrationPageRoute(localeId: string): Route {
  const locale = localeId?.split('-')[0] || 'en';

  return {
    path: '',
    canActivate: [IsLoggedInGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT][locale],
      },
      {
        path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT][locale],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_BENUTZER][locale], // Default tab
          },
          {
            path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_BENUTZER][locale],
            loadComponent: () =>
              import('./pages/benutzerverwaltung-page/benutzerverwaltung-page.component').then((m) => m.BenutzerverwaltungPageComponent),
            data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_BENUTZER },
            children: [
              // The Benutzer detail drawer, and the blank one at `details/new`. A child rather
              // than a sibling, so it renders into the overview's outlet and the list stays
              // mounted behind it — the same arrangement the Lexplus Konfiguration drawers use.
              {
                path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.DETAILS][locale] + '/:id',
                loadComponent: () =>
                  import(
                    './components/administration-benutzerverwaltung-benutzer-detail-drawer/administration-benutzerverwaltung-benutzer-detail-drawer.component'
                  ).then((m) => m.AdministrationBenutzerverwaltungBenutzerDetailDrawerComponent),
                data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_BENUTZER },
              },
            ],
          },
          {
            path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_BERECHTIGUNGEN][locale],
            loadComponent: () =>
              import('./pages/benutzerverwaltung-page/benutzerverwaltung-page.component').then((m) => m.BenutzerverwaltungPageComponent),
            data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_BERECHTIGUNGEN },
          },
          {
            path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_EINSTELLUNGEN][locale],
            loadComponent: () =>
              import('./pages/benutzerverwaltung-page/benutzerverwaltung-page.component').then((m) => m.BenutzerverwaltungPageComponent),
            data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_EINSTELLUNGEN },
          },
        ],
        canActivate: [IsLoggedInGuard, RequiresPermissionGuard],
        data: {
          routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT,
          requiredPermission: LexplusPermissionsEnum.UserAdministrationBenutzerverwaltung,
        },
      },
      {
        path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG][locale],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_FUNKTIONEN][locale], // Default tab
          },
          {
            path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_FUNKTIONEN][locale],
            loadComponent: () =>
              import('./pages/lexplus-konfiguration-page/lexplus-konfiguration-page.component').then(
                (m) => m.LexplusKonfigurationPageComponent,
              ),
            data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_FUNKTIONEN },
            children: [
              // The Funktion detail drawer, and the create form at `details/new`. A child
              // rather than a sibling, so it renders into the overview's outlet and the list
              // stays mounted behind it.
              {
                path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.DETAILS][locale] + '/:id',
                loadComponent: () =>
                  import(
                    './components/administration-konfiguration-funktion-detail-drawer/administration-konfiguration-funktion-detail-drawer.component'
                  ).then((m) => m.AdministrationKonfigurationFunktionDetailDrawerComponent),
                data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_FUNKTIONEN },
              },
            ],
          },
          {
            path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_THEMENBEREICHE][locale],
            loadComponent: () =>
              import('./pages/lexplus-konfiguration-page/lexplus-konfiguration-page.component').then(
                (m) => m.LexplusKonfigurationPageComponent,
              ),
            data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_THEMENBEREICHE },
            children: [
              // The same Themenbereich detail drawer the Standortverwaltung tab opens, over
              // this tab's list instead. A child rather than a sibling, for the same reason:
              // the list stays mounted behind the panel and keeps its state.
              {
                path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.DETAILS][locale] + '/:id',
                loadComponent: () =>
                  import(
                    './components/administration-konfiguration-themenbereich-detail-drawer/administration-konfiguration-themenbereich-detail-drawer.component'
                  ).then((m) => m.AdministrationKonfigurationThemenbereichDetailDrawerComponent),
                data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_THEMENBEREICHE, isGrouped: true },
              },
            ],
          },
          {
            path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_STANDORTVERWALTUNG][locale],
            loadComponent: () =>
              import('./pages/lexplus-konfiguration-page/lexplus-konfiguration-page.component').then(
                (m) => m.LexplusKonfigurationPageComponent,
              ),
            data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_STANDORTVERWALTUNG },
            children: [
              // The Themenbereich detail drawer. A child rather than a sibling, so it renders
              // into the overview's outlet and the Standort list stays mounted behind it —
              // which is what the design shows, and what keeps the list's state alive.
              {
                path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.DETAILS][locale] + '/:id',
                loadComponent: () =>
                  import(
                    './components/administration-konfiguration-themenbereich-detail-drawer/administration-konfiguration-themenbereich-detail-drawer.component'
                  ).then((m) => m.AdministrationKonfigurationThemenbereichDetailDrawerComponent),
                data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_STANDORTVERWALTUNG },
              },
              // The Funktionszuweisung drawer, and the blank one at `funktionszuweisung/new`.
              // Its own segment rather than the `details` one above, because the Standort's two
              // sub-tabs each open a drawer of their own and a shared segment could not tell
              // a Themenbereich id from a Funktion id.
              {
                path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.FUNKTIONSZUWEISUNG][locale] + '/:id',
                loadComponent: () =>
                  import(
                    './components/administration-konfiguration-funktion-zuweisung-drawer/administration-konfiguration-funktion-zuweisung-drawer.component'
                  ).then((m) => m.AdministrationKonfigurationFunktionZuweisungDrawerComponent),
                data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_STANDORTVERWALTUNG },
              },
              // The Benutzerverwaltung's Benutzer drawer, opened from the «Zugriff» sub-tab over the
              // Standort it was opened from. It reads the Benutzerverwaltung's endpoints, so it needs
              // that permission on top of this page's own.
              {
                path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_USER_MANAGEMENT_BENUTZER][locale] + '/:id',
                loadComponent: () =>
                  import(
                    './components/administration-benutzerverwaltung-benutzer-detail-drawer/administration-benutzerverwaltung-benutzer-detail-drawer.component'
                  ).then((m) => m.AdministrationBenutzerverwaltungBenutzerDetailDrawerComponent),
                canActivate: [RequiresPermissionGuard],
                data: {
                  routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_STANDORTVERWALTUNG,
                  requiredPermission: LexplusPermissionsEnum.UserAdministrationBenutzerverwaltung,
                },
              },
            ],
          },
          {
            path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_ANFORDERUNGEN_ANZEIGEFELDER][locale],
            loadComponent: () =>
              import('./pages/lexplus-konfiguration-page/lexplus-konfiguration-page.component').then(
                (m) => m.LexplusKonfigurationPageComponent,
              ),
            data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_ANFORDERUNGEN_ANZEIGEFELDER },
          },
          {
            path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_EINSTELLUNGEN][locale],
            loadComponent: () =>
              import('./pages/lexplus-konfiguration-page/lexplus-konfiguration-page.component').then(
                (m) => m.LexplusKonfigurationPageComponent,
              ),
            data: { routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG_EINSTELLUNGEN },
          },
        ],
        canActivate: [IsLoggedInGuard, RequiresPermissionGuard],
        data: {
          routeId: USER_ROUTES_IDS.USER_ADMINISTRATION_LEXPLUS_CONFIG,
          requiredPermission: LexplusPermissionsEnum.UserAdministrationLexpluskonfiguration,
        },
      },
    ],
  };
}
